import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import SearchCaseForm from "../SearchCase";
import { displayEmail, getCaseReferenceNumber, isAllowedAmountInput } from "../../utils";
import {
  getDisputeParticipants,
  createPaymentChallan,
  getCalculatedFee,
} from "../../service";

const PROBONO_ELIGIBLE_LIMIT = 500000;

const useStyles = makeStyles((theme) => ({
  paper: { padding: "2rem", border: "1px solid #E5E5E5" },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "1rem",
    marginBottom: "1rem",
    color: TEXT.primary,
  },
  label: {
    fontWeight: 500,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1),
    color: TEXT.secondary,
    display: "block",
  },
  selectedCaseCard: {
    padding: "1rem",
    backgroundColor: "#f5f5f5",
    borderRadius: "6px",
    border: "1px solid #e0e0e0",
    marginBottom: "1.5rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  feeBreakdown: {
    padding: "1rem",
    border: "1px solid #E5E5E5",
    borderRadius: "8px",
    backgroundColor: "#FAFAFA",
  },
  feeRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #E5E5E5",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  feeLabel: {
    fontSize: "0.875rem",
    color: TEXT.secondary,
  },
  feeValue: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: TEXT.strong,
  },
  partyRow: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    padding: "0.4rem 0",
    borderBottom: "1px solid #F0F0F0",
  },
}));

export default function CreatePaymentChallan({ onDone, onCancel }) {
  const classes = useStyles();

  const [selectedCase, setSelectedCase] = useState(null);
  const [sumInDispute, setSumInDispute] = useState("");
  const [totalFee, setTotalFee] = useState("");
  const [isProBono, setIsProBono] = useState(false);
  const [calc, setCalc] = useState(null); // {administrative_expense, arbitrator_fee, probono_fee}
  const [calculating, setCalculating] = useState(false);

  const [participants, setParticipants] = useState([]);
  const [selected, setSelected] = useState({}); // {profileId: true}
  const [amounts, setAmounts] = useState({}); // {profileId: "1234"}

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const isProBonoEligible = parseFloat(sumInDispute) <= PROBONO_ELIGIBLE_LIMIT;

  const handleCaseFound = (data) => {
    const d = data?.dispute_data || data;
    if (d && d.id) {
      setSelectedCase({
        id: d.id,
        tiac_custom_dispute_id: d.tiac_custom_dispute_id,
        name: d.name,
        category: d.category,
        customer_type: d.customer_type,
        request_ticket_number: d.request_ticket_number,
      });
    }
  };

  // Load claimants + respondents once a case is chosen.
  useEffect(() => {
    if (!selectedCase) return;
    getDisputeParticipants(selectedCase.id)
      .then((res) => setParticipants(res.data?.participants || []))
      .catch(() => setParticipants([]));
  }, [selectedCase]);

  // Reset Pro-Bono once the sum in dispute makes it ineligible.
  useEffect(() => {
    if (!isProBonoEligible && isProBono) setIsProBono(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProBonoEligible]);

  // Auto-calculate the fee breakdown from Sum in Dispute (debounced), and
  // keep Total Fee in sync with the calculated breakdown.
  useEffect(() => {
    const amount = parseFloat(sumInDispute);
    if (isNaN(amount) || amount <= 0) {
      setCalc(null);
      return;
    }
    const h = setTimeout(() => {
      setCalculating(true);
      getCalculatedFee(amount, isProBono)
        .then((res) => {
          const d = res.data || {};
          setCalc(d);
          const adminExpense = d.administrative_expense || 0;
          const arbitratorFee = d.arbitrator_fee || 0;
          const probonoFee = isProBono ? d.probono_fee || 0 : 0;
          setTotalFee(adminExpense + arbitratorFee + probonoFee);
          setCalculating(false);
        })
        .catch(() => {
          setCalc(null);
          setCalculating(false);
        });
    }, 500);
    return () => clearTimeout(h);
  }, [sumInDispute, isProBono]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  // Equal-split whenever the selection or total fee changes.
  useEffect(() => {
    const fee = parseFloat(totalFee);
    if (isNaN(fee) || selectedIds.length === 0) return;
    const each = Math.floor((fee / selectedIds.length) * 100) / 100;
    const next = {};
    let assigned = 0;
    selectedIds.forEach((id, i) => {
      // give the remainder to the last party so the total matches exactly
      next[id] = i === selectedIds.length - 1
        ? (fee - assigned).toFixed(2)
        : each.toFixed(2);
      assigned += each;
    });
    setAmounts(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedIds), totalFee]);

  const toggleParty = (id) =>
    setSelected((p) => ({ ...p, [id]: !p[id] }));

  const runningTotal = selectedIds.reduce(
    (sum, id) => sum + (parseFloat(amounts[id]) || 0), 0);
  const feeNum = parseFloat(totalFee) || 0;
  const mismatch = selectedIds.length > 0 && Math.abs(runningTotal - feeNum) > 0.01;

  const formatCurrency = (val) => {
    if (val === undefined || val === null) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const refNo = getCaseReferenceNumber(selectedCase);

  const submit = () => {
    setError("");
    if (!selectedCase) return setError("Please select a case.");
    if (!totalFee || feeNum <= 0) return setError("Enter a valid total fee.");
    if (selectedIds.length === 0) return setError("Select at least one party.");
    if (mismatch) return setError("Party amounts must add up to the total fee.");

    setSubmitting(true);
    createPaymentChallan({
      dispute_id: selectedCase.id,
      sum_in_dispute: sumInDispute || 0,
      total_fee: totalFee,
      admin_fee: calc?.administrative_expense ?? null,
      arbitrator_fee: calc?.arbitrator_fee ?? null,
      is_pro_bono: isProBono,
      parties: selectedIds.map((id) => ({ profile_id: Number(id), amount: amounts[id] })),
    })
      .then((res) => setSuccess(res.data))
      .catch((err) =>
        setError(err?.response?.data?.error || "Failed to create challan."))
      .finally(() => setSubmitting(false));
  };

  return (
    <Paper className={classes.paper}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          Create Payment Challan
        </Typography>
        {onCancel && (
          <Button
            size="small"
            onClick={onCancel}
            startIcon={<KeyboardBackspaceIcon fontSize="small" />}
            style={{ textTransform: "none" }}
          >
            Back
          </Button>
        )}
      </Box>
      <Box height="1rem" />

      {!selectedCase ? (
        <Box mb={3}>
          <Typography className={classes.sectionTitle}>Select Case</Typography>
          <SearchCaseForm
            classes={{
              paper: "",
              button: "",
            }}
            handleSearchSuccess={handleCaseFound}
          />
        </Box>
      ) : success ? (
        <Box textAlign="center" style={{ padding: "2.5rem 0" }}>
          <Box className={classes.selectedCaseCard}>
            <Box>
              <Typography style={{ fontWeight: 600 }}>
                Case No: {selectedCase.tiac_custom_dispute_id}
              </Typography>
              {refNo && (
                <Typography variant="body2" color="textSecondary" style={{ overflowWrap: "anywhere" }}>
                  {refNo.label}: {refNo.value}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                Title: {selectedCase.name}
              </Typography>
            </Box>
          </Box>

          <Typography variant="h6" style={{ fontWeight: 600, color: TEXT.success }}>
            Payment request created successfully.
          </Typography>

          <Typography style={{ marginTop: "0.75rem", fontSize: "1rem" }}>
            Challan No: <b>{success.challan_number || "—"}</b>
          </Typography>
          <Box height="1.5rem" />
          <Button
            variant="contained"
            color="primary"
            onClick={() => onDone && onDone(success)}
            style={{ textTransform: "none" }}
          >
            Done
          </Button>
        </Box>
      ) : (
        <>
          <Box className={classes.selectedCaseCard}>
            <Box>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Case No: {selectedCase.tiac_custom_dispute_id}
              </Typography>
              {refNo && (
                <Typography variant="body2" color="textSecondary" style={{ overflowWrap: "anywhere" }}>
                  {refNo.label}: {refNo.value}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                Title: {selectedCase.name}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => { setSelectedCase(null); setParticipants([]); setSelected({}); }}
            >
              Change
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Sum in Dispute (INR)</Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                value={sumInDispute}
                onChange={(e) => {
                  if (isAllowedAmountInput(e.target.value)) setSumInDispute(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Total Fee (INR)</Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                value={totalFee}
                onChange={(e) => {
                  if (isAllowedAmountInput(e.target.value)) setTotalFee(e.target.value);
                }}
                InputProps={{
                  endAdornment: calculating && <CircularProgress size={16} />,
                }}
              />
            </Grid>

            {isProBonoEligible && (
              <Grid item xs={12} style={{ paddingTop: 0, paddingBottom: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isProBono}
                      onChange={(e) => setIsProBono(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Pro-Bono"
                />
              </Grid>
            )}

            {calc && (
              <Grid item xs={12}>
                <Box className={classes.feeBreakdown}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
                    Fee Calculation Breakdown
                  </Typography>
                  <Box className={classes.feeRow}>
                    <Typography className={classes.feeLabel}>Administrative Expenses</Typography>
                    <Typography className={classes.feeValue}>
                      {formatCurrency(calc.administrative_expense)}
                    </Typography>
                  </Box>
                  <Box className={classes.feeRow}>
                    <Typography className={classes.feeLabel}>Arbitrator Fee</Typography>
                    <Typography className={classes.feeValue}>
                      {formatCurrency(calc.arbitrator_fee)}
                    </Typography>
                  </Box>
                  {isProBono && calc.probono_fee !== undefined && (
                    <Box className={classes.feeRow}>
                      <Typography className={classes.feeLabel}>Pro-Bono Fee</Typography>
                      <Typography className={classes.feeValue}>
                        {formatCurrency(calc.probono_fee)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>

          <Box height="1.25rem" />
          <Divider />
          <Box height="1rem" />
          <Typography className={classes.label}>Select Parties</Typography>
          {participants.length === 0 ? (
            <Typography style={{ fontSize: "0.85rem", color: TEXT.muted }}>
              No parties found for this case.
            </Typography>
          ) : (
            participants.map((p) => (
              <Box key={p.id} className={classes.partyRow}>
                <Checkbox checked={!!selected[p.id]} onChange={() => toggleParty(p.id)} size="small" color="primary" />
                <Box flex={1}>
                  <Typography style={{ fontSize: "0.9rem" }}>{p.name || displayEmail(p.email)}</Typography>
                  <Typography style={{ fontSize: "0.75rem", color: TEXT.muted }}>{displayEmail(p.email)}</Typography>
                </Box>
                {selected[p.id] && (
                  <TextField type="number" size="small" variant="outlined" label="Amount"
                             style={{ width: 140 }}
                             value={amounts[p.id] ?? ""}
                             onChange={(e) => {
                               const v = e.target.value;
                               if (!isAllowedAmountInput(v)) return;
                               setAmounts((a) => ({ ...a, [p.id]: v }));
                             }} />
                )}
              </Box>
            ))
          )}

          {error && (
            <Typography style={{ color: TEXT.error, fontSize: "0.85rem", marginTop: "0.75rem" }}>
              {error}
            </Typography>
          )}

          <Box height="1.25rem" />
          <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: "1.5rem" }}>
            {selectedIds.length > 0 && (
              <Typography style={{ fontSize: "0.9rem", textAlign: "right" }}>
                Running total: <b style={{ color: mismatch ? "#EB5757" : "#27AE60" }}>
                  ₹{runningTotal.toFixed(2)}
                </b>{" "}/ ₹{feeNum.toFixed(2)}
                {mismatch && (
                  <>
                    <br />
                    <span style={{ color: TEXT.error }}>Does not match total fee</span>
                  </>
                )}
              </Typography>
            )}
            <Button variant="contained" color="primary" disabled={submitting}
                    onClick={submit} style={{ textTransform: "none" }}>
              {submitting ? <CircularProgress size={20} /> : "Request Payment"}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}
